import React, {CSSProperties, memo, useEffect, useState} from 'react'
import {TextInput} from "atmosfair-ui";
import {
    validateAdditionalAddressInformation,
    validateCity,
    validateCountry,
    validateHouseNumber,
    validateStreet,
    validateZipCode
} from "../utils";
import CountriesDropdown, {CountriesDropdownProps} from "../../../molecules/dropdowns/CountriesDropdown";
import {INITIAL_SELECTED_COUNTRY, VALIDATE_FORM_STARTING_NUMBER} from "../../../constants/default-values";
import usePrevious from "../../../hooks/use-previous";
import {isEqual} from "lodash";

export interface AddressProps {
    country?: string | null,
    street: string,
    houseNumber: string,
    zipCode: string,
    city: string,
    additionalAddressInformation: string,
}

interface AddressFormProps {
    defaultValues?: AddressProps,
    countryRequired: CountriesDropdownProps["required"],
    countryStyle?: CSSProperties,
    streetRequired: boolean,
    streetStyle?: CSSProperties,
    houseNumberRequired: boolean,
    houseNumberStyle?: CSSProperties,
    zipCodeRequired: boolean,
    zipCodeStyle?: CSSProperties,
    cityRequired: boolean,
    cityStyle?: CSSProperties,
    additionalAddressInformationStyle?: CSSProperties,
    additionalAddressInformationRequired: boolean
    validateForm: number,
    onFormIsValid: (data: AddressProps) => void,
    disableForm?: boolean,
    showCountry: boolean,
}

interface FormErrorProps {
    streetError: boolean,
    houseNumberError: boolean,
    zipCodeError: boolean,
    cityError: boolean,
    additionalAddressInformationError: boolean,
    countryError: boolean,
}

const AddressForm = memo(function AddressForm({
                                                  countryRequired,
                                                  countryStyle = {flex: 2},
                                                  streetRequired,
                                                  streetStyle = {flex: 3},
                                                  houseNumberRequired,
                                                  houseNumberStyle = {flex: 1},
                                                  zipCodeRequired,
                                                  zipCodeStyle = {flex: 2},
                                                  cityRequired,
                                                  cityStyle = {flex: 2},
                                                  additionalAddressInformationRequired,
                                                  additionalAddressInformationStyle = {flex: 2},
                                                  validateForm,
                                                  onFormIsValid,
                                                  defaultValues,
                                                  disableForm,
                                                  showCountry,
                                              }: AddressFormProps) {
    const [formData, setFormData] = useState<AddressProps>({
        country: defaultValues?.country ?? INITIAL_SELECTED_COUNTRY?.name ?? null,
        street: defaultValues?.street ?? "",
        houseNumber: defaultValues?.houseNumber ?? "",
        zipCode: defaultValues?.zipCode ?? "",
        city: defaultValues?.city ?? "",
        additionalAddressInformation: defaultValues?.additionalAddressInformation ?? ""
    })
    const [formErrorData, setFormErrorData] = useState<FormErrorProps>({
        streetError: false,
        houseNumberError: false,
        zipCodeError: false,
        cityError: false,
        additionalAddressInformationError: false,
        countryError: false,
    })
    const {
        country,
        street,
        houseNumber,
        zipCode,
        city,
        additionalAddressInformation
    } = formData
    const {
        streetError,
        houseNumberError,
        zipCodeError,
        cityError,
        additionalAddressInformationError,
        countryError,
    } = formErrorData
    const previousDefaultValues = usePrevious(defaultValues)

    useEffect(() => {
        if (validateForm > VALIDATE_FORM_STARTING_NUMBER && _validateForm()) {
            let formData: AddressProps = {
                street: street.trim(),
                houseNumber: houseNumber.trim(),
                zipCode: zipCode.trim(),
                city: city.trim(),
                additionalAddressInformation: additionalAddressInformation.trim()
            }
            if (showCountry) formData['country'] = country
            onFormIsValid(formData)
        }
    }, [validateForm]);

    useEffect(() => {
        if (defaultValues && !isEqual(defaultValues, previousDefaultValues)) {
            const {country, street, houseNumber, zipCode, additionalAddressInformation, city} = defaultValues
            setFormData({country, street, houseNumber, zipCode, additionalAddressInformation, city})
        }
    }, [defaultValues]);

    const _updateFormData = <K extends keyof AddressProps>(key: K, payload: AddressProps[K]) => setFormData({
        ...formData,
        [key]: payload
    })

    const _validateForm = () => {
        let validStreet = true
        let validHouseNumber = true
        let validZipCode = true
        let validCity = true
        let validAdditionalAddressInformation = true
        let validCountry = true

        if (streetRequired || street.length > 0) validStreet = validateStreet(street)
        if (houseNumberRequired || houseNumber.length > 0) validHouseNumber = validateHouseNumber(houseNumber)
        if (zipCodeRequired || zipCode.length > 0) validZipCode = validateZipCode(zipCode)
        if (cityRequired || city.length > 0) validCity = validateCity(city)
        if (additionalAddressInformationRequired || additionalAddressInformation.length > 0) validAdditionalAddressInformation = validateAdditionalAddressInformation(additionalAddressInformation)
        if (countryRequired) validCountry = validateCountry(country)

        setFormErrorData({
            streetError: !validStreet,
            houseNumberError: !validHouseNumber,
            zipCodeError: !validZipCode,
            cityError: !validCity,
            additionalAddressInformationError: !validAdditionalAddressInformation,
            countryError: !validCountry
        })

        return [
            validStreet,
            validHouseNumber,
            validZipCode,
            validCity,
            validAdditionalAddressInformation,
            validCity,
        ].every(Boolean)
    }

    return <div className={'form-row'}>
        <TextInput
            readOnly={disableForm}
            aria-errormessage={"ERRORS.COMMON.STREET"}
            error={streetError}
            value={street}
            onBlur={event => _updateFormData("street", event.target.value.trim())}
            onChange={event => _updateFormData("street", event.target.value)}
            containerStyle={streetStyle}
            required={streetRequired}
            placeholder={"GENERAL.STREET"}
            label={"GENERAL.STREET"}/>
        <TextInput
            readOnly={disableForm}
            aria-errormessage={"ERRORS.COMMON.HOUSE_NUMBER"}
            error={houseNumberError}
            value={houseNumber}
            onBlur={event => _updateFormData("houseNumber", event.target.value.trim())}
            onChange={event => _updateFormData("houseNumber", event.target.value)}
            containerStyle={houseNumberStyle}
            required={houseNumberRequired}
            placeholder={"GENERAL.HOUSE_NUMBER"}
            label={"GENERAL.HOUSE_NUMBER"}/>
        <TextInput
            readOnly={disableForm}
            aria-errormessage={"ERRORS.COMMON.ZIP_CODE"}
            error={zipCodeError}
            value={zipCode}
            onBlur={event => _updateFormData("zipCode", event.target.value.trim())}
            onChange={event => _updateFormData("zipCode", event.target.value)}
            containerStyle={zipCodeStyle}
            required={zipCodeRequired}
            placeholder={"GENERAL.ZIP_CODE"}
            label={"GENERAL.ZIP_CODE"}/>
        <TextInput
            readOnly={disableForm}
            aria-errormessage={"ERRORS.COMMON.CITY"}
            error={cityError}
            value={city}
            onBlur={event => _updateFormData("city", event.target.value.trim())}
            onChange={event => _updateFormData("city", event.target.value)}
            containerStyle={cityStyle}
            required={cityRequired}
            placeholder={"GENERAL.CITY"}
            label={"GENERAL.CITY"}/>
        <TextInput
            readOnly={disableForm}
            required={additionalAddressInformationRequired}
            aria-errormessage={"ERRORS.COMMON.ADDITIONAL_ADDRESS_INFORMATION"}
            error={additionalAddressInformationError}
            onBlur={event => _updateFormData("additionalAddressInformation", event.target.value.trim())}
            onChange={event => _updateFormData("additionalAddressInformation", event.target.value)}
            containerStyle={additionalAddressInformationStyle}
            value={additionalAddressInformation}
            placeholder={"GENERAL.ADDITIONAL_ADDRESS_INFORMATION"}
            label={"GENERAL.ADDITIONAL_ADDRESS_INFORMATION"}/>
        {showCountry && <CountriesDropdown
            readOnly={disableForm}
            country={country}
            required={countryRequired}
            error={countryError}
            style={countryStyle}
            setSelection={selection => _updateFormData("country", selection)}/>}
    </div>
})

export default AddressForm